import { useState, useEffect, createContext, useContext } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import Sidebar from "./Sidebar";
import { Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AppLayout is used in TWO modes:
 *
 * 1. **Router-level (preferred)**: wrapped as a parent layout route in App.js.
 *    <Route element={<AppLayout />}>
 *      <Route path="/dashboard" element={<Dashboard />} />
 *      ...
 *    </Route>
 *    This mounts AppLayout (and its Sidebar) ONCE and keeps it mounted across
 *    route changes. Pages render via <Outlet />. No sidebar remount = no flicker.
 *
 * 2. **Legacy per-page**: a page can still wrap itself in <AppLayout>...</AppLayout>.
 *    When nested inside a router-level AppLayout, the inner one becomes a passthrough
 *    (uses context to detect nesting) and just renders its children — no double sidebar,
 *    no double auth check. This lets us migrate the app incrementally without touching
 *    all pages at once.
 *
 * The user object is exposed via context so pages using the function-children pattern
 * (e.g. Messaging: <AppLayout>{({user}) => <X user={user}/>}</AppLayout>) keep working.
 */

const AppLayoutContext = createContext({ user: null, isNested: false });

export function useAppLayoutUser() {
  return useContext(AppLayoutContext).user;
}

export default function AppLayout({ children }) {
  const parent = useContext(AppLayoutContext);

  // NESTED CASE — a parent AppLayout already exists (router-level).
  // Just render the children with the user from context. No sidebar, no auth fetch.
  if (parent.isNested) {
    if (typeof children === "function") return children({ user: parent.user });
    return <>{children}</>;
  }

  return <AppLayoutOuter>{children}</AppLayoutOuter>;
}

function AppLayoutOuter({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileGateChecked, setProfileGateChecked] = useState(false);
  const [profileCompleted, setProfileCompleted] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: "include",
        });
        if (response.ok) {
          setUser(await response.json());
        } else {
          navigate("/auth");
        }
      } catch (error) {
        navigate("/auth");
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, [navigate]);

  // Onboarding gate — checks once on mount (to load status) and re-fires on
  // every route change so a user who clears profile_completed mid-session is
  // still redirected to /profile from any other authenticated route.
  useEffect(() => {
    if (!user) return;
    // Skip check while on /profile itself.
    if (location.pathname === "/profile") return;
    // First run: fetch the flag; subsequent runs on route change use cached value.
    if (!profileGateChecked) {
      (async () => {
        try {
          const res = await fetch(`${BACKEND_URL}/api/company-profile`, { credentials: "include" });
          if (res.ok) {
            const data = await res.json();
            const completed = !!data?.profile?.profile_completed;
            setProfileCompleted(completed);
            if (!completed) navigate("/profile", { replace: true });
          }
        } catch { /* ignore — will retry on next mount */ }
        setProfileGateChecked(true);
      })();
    } else if (!profileCompleted) {
      // Already know they're not completed — redirect on any nav to non-/profile route.
      navigate("/profile", { replace: true });
    }
  }, [user, profileGateChecked, profileCompleted, location.pathname, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <AppLayoutContext.Provider value={{ user, isNested: true }}>
      <div className="min-h-screen bg-slate-50 flex">
        <Sidebar user={user} />
        <main className="flex-1 md:ml-0 mt-14 md:mt-0 min-h-screen">
          <div className="p-4 md:p-8">
            {typeof children === "function"
              ? children({ user })
              : children ?? <Outlet />}
          </div>
        </main>
      </div>
    </AppLayoutContext.Provider>
  );
}
